import { Lazy } from '../chat/lazy';
import { fromIsoString, toIsoString } from '../chat/iso-date';
import type { WorkflowRunData } from './types';
import type { SerializedWorkflowRun } from './types';
import type { Workflow } from './workflow';
import type { WorkflowRunNodeExecution } from './workflow-run-node-execution';
import type { WorkflowRunStep } from './workflow-run-step';

function optionalDate(iso: string | null): Date | null {
  if (iso === null) {
    return null;
  }
  return fromIsoString(iso);
}

function optionalIso(d: Date | null): string | null {
  if (d === null) {
    return null;
  }
  return toIsoString(d);
}

export type WorkflowRunHydration = {
  readonly loadWorkflow: () => Promise<Workflow | null>;
  readonly loadNodeExecutions: () => Promise<
    readonly WorkflowRunNodeExecution[]
  >;
  readonly loadRunSteps: () => Promise<readonly WorkflowRunStep[]>;
  readonly preloadedWorkflow?: Workflow | null;
  readonly preloadedNodeExecutions?: readonly WorkflowRunNodeExecution[];
  readonly preloadedRunSteps?: readonly WorkflowRunStep[];
};

/**
 * One execution of a workflow (`workflow_runs`). Run I/O is not held here: it
 * lives on the `system/run-input` node's root step instance, via
 * {@link WorkflowRunStep}.
 */
export class WorkflowRun {
  private readonly _data: WorkflowRunData;
  readonly workflow: Lazy<Workflow | null>;
  readonly nodeExecutions: Lazy<readonly WorkflowRunNodeExecution[]>;
  readonly steps: Lazy<readonly WorkflowRunStep[]>;

  private constructor(data: WorkflowRunData, hydration: WorkflowRunHydration) {
    this._data = data;
    this.workflow = new Lazy(
      hydration.loadWorkflow,
      hydration.preloadedWorkflow
    );
    this.nodeExecutions = new Lazy(
      hydration.loadNodeExecutions,
      hydration.preloadedNodeExecutions
    );
    this.steps = new Lazy(hydration.loadRunSteps, hydration.preloadedRunSteps);
  }

  get id(): string {
    return this._data.id;
  }

  get ownerId(): string {
    return this._data.ownerId;
  }

  get ownerOrgId(): string {
    return this._data.ownerOrgId;
  }

  get workflowId(): string {
    return this._data.workflowId;
  }

  get triggerType(): string {
    return this._data.triggerType;
  }

  get triggerId(): string {
    return this._data.triggerId;
  }

  get idempotencyKey(): string | null {
    return this._data.idempotencyKey;
  }

  get status(): string {
    return this._data.status;
  }

  get error(): string | null {
    return this._data.error;
  }

  get startedAt(): Date | null {
    return this._data.startedAt;
  }

  get finishedAt(): Date | null {
    return this._data.finishedAt;
  }

  get createdAt(): Date {
    return this._data.createdAt;
  }

  get updatedAt(): Date {
    return this._data.updatedAt;
  }

  get runData(): WorkflowRunData {
    return { ...this._data };
  }

  serialize(): SerializedWorkflowRun {
    return {
      id: this._data.id,
      ownerId: this._data.ownerId,
      ownerOrgId: this._data.ownerOrgId,
      workflowId: this._data.workflowId,
      triggerType: this._data.triggerType,
      triggerId: this._data.triggerId,
      idempotencyKey: this._data.idempotencyKey,
      status: this._data.status,
      error: this._data.error,
      startedAt: optionalIso(this._data.startedAt),
      finishedAt: optionalIso(this._data.finishedAt),
      createdAt: toIsoString(this._data.createdAt),
      updatedAt: toIsoString(this._data.updatedAt),
    };
  }

  static fromSerialized(
    serialized: SerializedWorkflowRun,
    hydration: WorkflowRunHydration
  ): WorkflowRun {
    const data: WorkflowRunData = {
      id: serialized.id,
      ownerId: serialized.ownerId,
      ownerOrgId: serialized.ownerOrgId,
      workflowId: serialized.workflowId,
      triggerType: serialized.triggerType,
      triggerId: serialized.triggerId,
      idempotencyKey: serialized.idempotencyKey,
      status: serialized.status,
      error: serialized.error,
      startedAt: optionalDate(serialized.startedAt),
      finishedAt: optionalDate(serialized.finishedAt),
      createdAt: fromIsoString(serialized.createdAt),
      updatedAt: fromIsoString(serialized.updatedAt),
    };
    return new WorkflowRun(data, hydration);
  }
}

export async function resolveNoWorkflowForRun(): Promise<Workflow | null> {
  return null;
}

export async function resolveEmptyRunNodeExecutions(): Promise<
  readonly WorkflowRunNodeExecution[]
> {
  return [];
}

export async function resolveEmptyRunSteps(): Promise<
  readonly WorkflowRunStep[]
> {
  return [];
}
